import { useState, useEffect } from 'react'
import type { DestMasterRequest, DestMasterResponse } from '../types/master'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

async function fetchDestinationsAPI(): Promise<DestMasterResponse[]> {
  const response = await fetch(`${API_BASE_URL}/api/dest`, {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
  return response.json()
}

async function createDestinationAPI(data: DestMasterRequest): Promise<DestMasterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/dest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function updateDestinationAPI(id: number, data: DestMasterRequest): Promise<DestMasterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/dest/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export const useDestMaster = () => {
  const [destinations, setDestinations] = useState<DestMasterResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDestinations = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('Fetching destinations from:', `${API_BASE_URL}/api/dest`)
      
      const data = await fetchDestinationsAPI()
      console.log('Destinations fetched successfully:', data.length)
      setDestinations(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      console.error('Error fetching destinations:', errorMessage)
      setError(errorMessage)
      setDestinations([])
    } finally {
      setLoading(false)
    }
  }

  const createDestination = async (data: DestMasterRequest): Promise<DestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const newDestination = await createDestinationAPI(data)
      setDestinations(prev => [...prev, newDestination])
      return newDestination
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateDestination = async (
    id: number,
    data: DestMasterRequest
  ): Promise<DestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const updatedDestination = await updateDestinationAPI(id, data)
      setDestinations(prev => prev.map(dest => (dest.id === id ? updatedDestination : dest)))
      return updatedDestination
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteDestination = async (id: number): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/dest/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to delete destination: ${response.statusText}`)
      }

      setDestinations(prev => prev.filter(dest => dest.id !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getDestinationById = async (id: number): Promise<DestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/dest/${id}`, {
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch destination: ${response.statusText}`)
      }
      const data = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const loadDestinations = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('Loading destinations...')

        const response = await fetch(`${API_BASE_URL}/api/dest`, {
          credentials: 'include',
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (isMounted) {
          console.log('Destinations loaded successfully:', data.length)
          setDestinations(data)
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
          console.error('Error loading destinations:', errorMessage)
          setError(errorMessage)
          setDestinations([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadDestinations()

    return () => {
      isMounted = false
    }
  }, [])

  return {
    destinations,
    loading,
    error,
    refetch: fetchDestinations,
    createDestination,
    updateDestination,
    deleteDestination,
    getDestinationById,
  }
}
